import {useCallback, useEffect, useState} from 'react';
import {
  GenderEnum,
  IndustryEnum,
  MemberOfPadelClubEnum,
  NewUserModel, NprpRatingEnum,
  PlayingFrequencyEnum,
  SkillLevelEnum,
  UserProfileModel,
  UserProfileModelForSend,
  UserRequests
} from '@/app/api/user';
import {route} from '@/app/routes/AppRoutes';
import {useNavigate} from 'react-router-dom';
import usePhotoUpload from '@/app/utils/use_photo_upload';
import Cookies from 'js-cookie';
import {CookiesKey} from '@/app/utils/cookies';
import {toSnakeUpperCase, fromEnumKeyToEnumValue} from '@/app/utils/custom_function';
import { logEventSafe, platform } from '@/app/utils/analytics';

// Import types
import {ProfileState, ProfileActions} from './types';

/**
 * List of allowed keys for user profile update
 * This ensures we only send valid fields to the API
 */
const ALLOWED_KEYS: (keyof UserProfileModel)[] = [
  'username',
  'imageLink',
  'ntrpRating',
  'skillLevel',
  'city',
  'gender',
  'showGenderInProfile',
  'padelClubMembership',
  'playingDuration',
  'playingFrequency',
  'aboutYourself',
  'industry',
  'instagram',
  'linkedin',
  'rocketSportsExperience',
  'dateOfBirth',
  'name'
];

/**
 * Helper function to pick only allowed fields from an object
 * @param obj - Source object
 * @param keys - Array of allowed keys
 * @returns New object with only allowed keys
 */
function pickAllowedFields<T extends object, K extends keyof T>(obj: T, keys: K[]): Pick<T, K> {
  const result = {} as Pick<T, K>;
  keys.forEach((key) => {
    if (obj[key] !== undefined) result[key] = obj[key];
  });
  return result;
}

/**
 * Custom hook for managing profile state and actions
 * @returns Profile state and actions
 */
const useProfile = (): { state: ProfileState } & ProfileActions => {
  const {pickPhoto, uploadBase64} = usePhotoUpload();
  const navigate = useNavigate();

  // Initialize state with default values
  const [state, setState] = useState<ProfileState>({
    hasError: undefined,
    hasLoading: false,
    isEdit: false,
    user: undefined,
    editedUser: undefined,
    isOpenQuestionnaire: false,
    photo: undefined,
    showWantToLeave: false
  });

  /**
   * Helper function to update state
   * @param updates - Partial state updates
   */
  const updateState = useCallback((updates: Partial<ProfileState>) => {
    setState((prev) => ({...prev, ...updates}));
  }, []);

  /**
   * Initialize profile data
   * Fetches current user data from API
   */
  const init = useCallback(async () => {
    updateState({hasLoading: true, hasError: undefined});
    try {
      const response = await UserRequests.getCurrentUser();

      const rawPlayFrequency = response.user.playFrequency as unknown as string | undefined;
      const normalizedPlayFrequency: PlayingFrequencyEnum | undefined = (() => {
        if (!rawPlayFrequency) return undefined;
        const values = PlayingFrequencyEnum.values as unknown as string[];
        if (values.includes(rawPlayFrequency)) {
          return rawPlayFrequency as PlayingFrequencyEnum;
        }
        return fromEnumKeyToEnumValue(PlayingFrequencyEnum, rawPlayFrequency) as PlayingFrequencyEnum | undefined;
      })();

      const eu = {
        ...response,
        ...response.user,
        showDateOfBirthInProfile: false,
        showGenderInProfile: false,
        industry: response.industry || undefined,
        user: {
          ...response.user,
          playFrequency: normalizedPlayFrequency
        }
      };

      updateState({
        user: response,
        editedUser: eu,
      });
    } catch (error: any) {
      console.error('Failed to fetch user profile:', error);
      updateState({hasError: error.message || 'Failed to load profile'});
    } finally {
      updateState({hasLoading: false});
    }
  }, [updateState]);

  // Load profile data on component mount
  useEffect(() => {
    init();
  }, [init]);

  // Navigation actions
  const goToOnboarding = useCallback(() => navigate(route.onboarding), [navigate]);

  // UI state actions
  const setIsEdit = useCallback((isEdit: boolean) => updateState({isEdit}), [updateState]);
  const toggleOpenQuestionnaire = useCallback(() => {
    updateState({isOpenQuestionnaire: !state.isOpenQuestionnaire});
  }, [state.isOpenQuestionnaire, updateState]);
  const toggleShowWantToLeave = useCallback(() => {
    updateState({showWantToLeave: !state.showWantToLeave});
  }, [state.showWantToLeave, updateState]);

  // Profile field update actions
  const setName = useCallback((name: string) => {
    if (state.editedUser?.user.username === name) return;
    updateState({
      editedUser: {
        ...state.editedUser,
        id: state.editedUser?.id ?? 0,
        user: {
          ...state.editedUser?.user,
          id: state.editedUser?.user.id ?? 0,
          username: name
        }
      }
    });
  }, [state.editedUser, updateState]);

  const onSelect = useCallback((city: string) => {
    updateState({
      editedUser: {
        ...state.editedUser,
        id: state.editedUser?.id ?? 0,
        user: state.editedUser?.user ?? {id: 0},
        city
      }
    });
  }, [state.editedUser, updateState]);

  const setPadelSkillLevel = useCallback((level: SkillLevelEnum) => {
    updateState({
      editedUser: {
        ...state.editedUser,
        id: state.editedUser?.id ?? 0,
        user: state.editedUser?.user ?? {id: 0},
        skillLevel: level
      }
    });
  }, [state.editedUser, updateState]);

  const setNtrpRating = useCallback((level: NprpRatingEnum
  ) => {
    updateState({
      editedUser: {
        ...state.editedUser,
        id: state.editedUser?.id ?? 0,
        user: state.editedUser?.user ?? {id: 0},
        ntrpRating: level
      }
    });
  }, [state.editedUser, updateState]);

  const setPadelClubMembership = useCallback((membership: MemberOfPadelClubEnum) => {
    updateState({
      editedUser: {
        ...state.editedUser,
        id: state.editedUser?.id ?? 0,
        user: state.editedUser?.user ?? {id: 0},
        padelClubMembership: membership
      }
    });
  }, [state.editedUser, updateState]);

  const setPlayingFrequency = useCallback((freq: PlayingFrequencyEnum) => {
    updateState({
      editedUser: {
        ...state.editedUser,
        id: state.editedUser?.id ?? 0,
        user: {
          ...state.editedUser?.user ?? {id: 0},
          playFrequency: freq
        }
      }
    });
  }, [state.editedUser, updateState]);

  const setDateOfBirth = useCallback((date: Date) => {
    updateState({
      editedUser: {
        ...state.editedUser,
        id: state.editedUser?.id ?? 0,
        user: state.editedUser?.user ?? {id: 0},
        dateOfBirth: date.toISOString().slice(0, 10)
      }
    });
  }, [state.editedUser, updateState]);

  const toggleShowInProfileDateOfBirth = useCallback(() => {
    // Note: showDateOfBirthInProfile is not part of NewUserModel, 
    // this function is kept for interface compatibility but doesn't update editedUser
  }, []);

  const setGender = useCallback((gender: GenderEnum) => {
    updateState({
      editedUser: {
        ...state.editedUser,
        id: state.editedUser?.id ?? 0,
        user: state.editedUser?.user ?? {id: 0},
        gender
      }
    });
  }, [state.editedUser, updateState]);

  const setAboutMe = useCallback((aboutMe: string) => {
    if (state.editedUser?.aboutYourself === aboutMe) return;
    updateState({
      editedUser: {
        ...state.editedUser,
        id: state.editedUser?.id ?? 0,
        user: state.editedUser?.user ?? {id: 0},
        aboutYourself: aboutMe
      }
    });
  }, [state.editedUser, updateState]);

  /**
   * Toggle industry selection
   * Handles adding/removing industries from comma-separated list
   */
  const setIndustry = useCallback((industry: IndustryEnum) => {
    const oldIndustries = state.editedUser?.industry || '';
    const industries = oldIndustries ? oldIndustries.split(',') : [];
    const exists = industries.includes(industry);

    const newIndustries = exists
      ? industries.filter(i => i !== industry)
      : [...industries, industry];

    // Filter out empty strings and join with commas
    const industryString = newIndustries.filter(Boolean).join(',');

    updateState({
      editedUser: {
        ...state.editedUser,
        id: state.editedUser?.id ?? 0,
        user: state.editedUser?.user ?? {id: 0},
        industry: industryString
      }
    });
  }, [state.editedUser, updateState]);

  const setInstagram = useCallback((instagram: string) => {
    if (state.editedUser?.instagram === instagram) return;
    updateState({
      editedUser: {
        ...state.editedUser,
        id: state.editedUser?.id ?? 0,
        user: state.editedUser?.user ?? {id: 0},
        instagram
      }
    });
  }, [state.editedUser, updateState]);

  const setLinkedin = useCallback((linkedin: string) => {
    if (state.editedUser?.linkedin === linkedin) return;
    updateState({
      editedUser: {
        ...state.editedUser,
        id: state.editedUser?.id ?? 0,
        user: state.editedUser?.user ?? {id: 0},
        linkedin
      }
    });
  }, [state.editedUser, updateState]);

  /**
   * Handle photo upload
   * Picks a photo and updates state with base64 data
   */
  const uploadPhoto = useCallback(async () => {
    try {
      const photo = await pickPhoto();
      if (photo) {
        updateState({photo});
      }
    } catch (error) {
      console.error('Failed to upload photo:', error);
      updateState({hasError: 'Failed to upload photo'});
    }
  }, [pickPhoto, updateState]);

  /**
   * Save profile changes
   * Uploads photo if changed and sends profile data to API
   */
  const onSave = useCallback(async () => {
    updateState({hasLoading: true, hasError: undefined});
    try {
      if (!state.editedUser) {
        throw new Error('No profile data to save');
      }

      // Upload photo if changed
      let imgUrl = state.editedUser.user.imageLink;
      if (state.photo) {
        imgUrl = await uploadBase64(state.photo);
      }

      // Prepare payload with allowed fields only
      const userProfileData: UserProfileModel = {
        username: state.editedUser.user.username,
        imageLink: imgUrl,
        name: state.editedUser.user.username,
        ntrpRating: state.editedUser.ntrpRating,
        skillLevel: state.editedUser.skillLevel,
        city: state.editedUser.city,
        gender: state.editedUser.gender,
        showGenderInProfile: state.editedUser.showGenderInProfile,
        padelClubMembership: state.editedUser.padelClubMembership,
        playingDuration: state.editedUser.playingDuration,
        playingFrequency: state.editedUser.user.playFrequency,
        aboutYourself: state.editedUser.aboutYourself,
        industry: state.editedUser.industry,
        instagram: state.editedUser.instagram,
        linkedin: state.editedUser.linkedin,
        rocketSportsExperience: state.editedUser.rocketSportsExperience,
        dateOfBirth: state.editedUser.dateOfBirth,
        showDateOfBirthInProfile: state.editedUser.showGenderInProfile
      };

      const payload = pickAllowedFields(userProfileData, ALLOWED_KEYS);

      // Compute fields changed for analytics
      const prev = state.user;
      const prevComparable: Partial<UserProfileModel> | undefined = prev ? {
        username: prev.user?.username,
        imageLink: prev.user?.imageLink,
        name: prev.user?.username,
        ntrpRating: prev.ntrpRating,
        skillLevel: prev.skillLevel,
        city: prev.city,
        gender: prev.gender,
        showGenderInProfile: prev.showGenderInProfile,
        padelClubMembership: prev.padelClubMembership,
        playingDuration: prev.playingDuration,
        playingFrequency: prev.user?.playFrequency,
        aboutYourself: prev.aboutYourself,
        industry: prev.industry,
        instagram: prev.instagram,
        linkedin: prev.linkedin,
        rocketSportsExperience: prev.rocketSportsExperience,
        dateOfBirth: prev.dateOfBirth,
      } : undefined;

      const fields_changed: string[] = [];
      if (prevComparable) {
        (Object.keys(payload) as (keyof UserProfileModel)[]).forEach((k) => {
          const newVal = (payload as any)[k];
          const oldVal = (prevComparable as any)[k];
          if (newVal !== undefined && newVal !== oldVal) fields_changed.push(k as string);
        });
      }

      // Send to API
      const dataToSend: UserProfileModelForSend = {
        ...payload,
        playingFrequency: toSnakeUpperCase(payload.playingFrequency ?? '')
      };

      // Send to API
      await UserRequests.updateUser(dataToSend);

      // Log profile update
      if (fields_changed.length > 0) {
        await logEventSafe('padel_profile_update', { fields_changed: JSON.stringify(fields_changed) });
      }

      // Exit edit mode and refresh data
      updateState({isEdit: false});
      await init();
    } catch (error: any) {
      console.error('Failed to save profile:', error);
      updateState({hasError: error.message || 'Failed to save profile'});
    } finally {
      updateState({hasLoading: false});
    }
  }, [state.editedUser, state.photo, uploadBase64, init, updateState]);

  /**
   * Handle logout
   * Removes auth token and navigates to signup page
   */
  const onLogout = useCallback(() => {
    Cookies.remove(CookiesKey.token);
    // Log logout
    logEventSafe('padel_logout', { platform });
    navigate(route.signup);
  }, [navigate]);

  return {
    state,
    goToOnboarding,
    setIsEdit,
    setName,
    onSelect,
    setPadelSkillLevel,
    setNtrpRating,
    setPadelClubMembership,
    setPlayingFrequency,
    setDateOfBirth,
    toggleShowInProfileDateOfBirth,
    setGender,
    setAboutMe,
    setIndustry,
    setInstagram,
    setLinkedin,
    toggleOpenQuestionnaire,
    uploadPhoto,
    onSave,
    onLogout,
    toggleShowWantToLeave
  };
};

export default useProfile;